import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Server, Cpu, MemoryStick, HardDrive, Plus, ArrowRight } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { Button, Card, CardBody } from '@vnetwork/ui/primitives';
import { useInstanceList, useQuotas, type Instance } from '@/api/opennebula';
import { instanceStateKey, instanceTone, instanceLabel, instanceBucket, instanceIps } from './shared';
// Reuse the Services dashboard's provisioned-card styling so recent instances
// render as the same clickable card grid (consistent look, no duplicate CSS).
import cardStyles from '../Services.module.css';

export function ComputeDashboard() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: instances = [] } = useInstanceList(serviceId);
  const { data: quotas = {} } = useQuotas(serviceId);
  const base = `/services/compute/${serviceId}`;

  // Status breakdown + aggregate resource consumption from the instance list.
  const stats = useMemo(() => {
    const buckets = { running: 0, stopped: 0, error: 0, other: 0 };
    let cores = 0, memory = 0, volume = 0;
    for (const i of instances) {
      buckets[instanceBucket(instanceStateKey(i.state, i.status))]++;
      const r = i.metadata?.resources;
      cores += r?.cores ?? 0;
      memory += r?.memory ?? 0;
      volume += r?.volumes ?? 0;
    }
    return { buckets, cores, memory, volume };
  }, [instances]);

  const usage = [
    { icon: Cpu, label: 'vCPU', used: stats.cores, max: quotas.cores, unit: '' },
    { icon: MemoryStick, label: 'Memory', used: stats.memory, max: quotas.memory, unit: 'GiB' },
    { icon: HardDrive, label: 'Volume', used: stats.volume, max: quotas.volumes, unit: 'GiB' },
    { icon: Server, label: 'Instances', used: instances.length, max: quotas.instances, unit: '' },
  ];

  // "Recent" = newest first. Prefer the created/updated timestamp; fall back to
  // the numeric OpenNebula VM id (monotonically increasing) when the API omits it.
  const recencyWeight = (i: Instance) => {
    const t = Date.parse(i.created_at ?? i.updated_at ?? '');
    if (!Number.isNaN(t)) return t;
    const n = Number(i.id);
    return Number.isNaN(n) ? 0 : n;
  };
  const recent = [...instances]
    .sort((a, b) => recencyWeight(b) - recencyWeight(a))
    .slice(0, 6);

  return (
    <Container size="xl">
      <PageHeader icon={<Server size={22} />} title="Overview" description="Virtual machines with networking, load balancers and backups." />
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section title="Status & usage" description="Status and resource consumption for this service." style={{ marginBottom: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-3)' }}>
          <StatusCard label="Running" value={stats.buckets.running} tone="success" />
          <StatusCard label="Powered off" value={stats.buckets.stopped} tone="muted" />
          <StatusCard label="Error" value={stats.buckets.error} tone="danger" />
          <StatusCard label="Other" value={stats.buckets.other} tone="warning" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' }}>
          {usage.map((u) => {
            const Icon = u.icon;
            const pct = u.max ? Math.min(100, Math.round((u.used / u.max) * 100)) : null;
            return (
              <Card key={u.label} flat>
                <CardBody>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--vn-fg-muted)', marginBottom: 6, fontSize: 'var(--vn-font-size-sm)' }}>
                    <Icon size={16} /> {u.label}
                  </div>
                  <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700 }}>
                    {u.used}
                    {u.max != null ? <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400, fontSize: 'var(--vn-font-size-md)' }}> / {u.max}</span> : ''}{' '}
                    <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', fontWeight: 400 }}>{u.unit}</span>
                  </div>
                  {pct != null && (
                    <div style={{ marginTop: 8, height: 6, borderRadius: 'var(--vn-radius-full)', background: 'var(--vn-bg-muted)', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: pct > 90 ? 'var(--vn-danger-solid)' : 'var(--vn-accent-solid)' }} />
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      </Section>

      <Section
        title="Recent instances"
        description="Your most recently listed virtual machines."
        style={{ marginBottom: 0 }}
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            <Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/instances/create`)}>Create instance</Button>
            <Button size="sm" variant="secondary" endIcon={<ArrowRight size={15} />} onClick={() => navigate(`${base}/instances`)}>View all</Button>
          </div>
        }
      >
        {recent.length === 0 ? (
          <Card>
            <CardBody>
              <EmptyState
                icon={<Server size={28} />}
                title="No instances yet"
                description="Launch your first virtual machine to get started."
                actions={
                  <Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/instances/create`)}>
                    Create instance
                  </Button>
                }
              />
            </CardBody>
          </Card>
        ) : (
          <div className={cardStyles.grid}>
            {recent.map((i: Instance) => (
              <RecentInstanceCard
                key={i.id}
                instance={i}
                onOpen={() => navigate(`${base}/instances/${i.id}`)}
              />
            ))}
          </div>
        )}
      </Section>
      </div>
    </Container>
  );
}

/**
 * Recent-instance tile — mirrors the Services dashboard's ProvisionedCard so the
 * compute overview and the services overview share one clickable card grammar:
 * icon + title + sub on top, status pill on the right, primary IP + "Open" foot.
 */
function RecentInstanceCard({ instance, onOpen }: { instance: Instance; onOpen: () => void }) {
  const key = instanceStateKey(instance.state, instance.status);
  const { publicIp, privateIp } = instanceIps(instance.metadata);
  const ip = publicIp || privateIp;
  const sub = instance.metadata?.image?.name || instance.metadata?.flavor || '';
  return (
    <div
      className={`${cardStyles.card} ${cardStyles.cardClickable}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className={cardStyles.cardHead}>
        <span className={`${cardStyles.cardIcon} ${cardStyles.cardIconActive}`}>
          <Server size={19} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className={cardStyles.cardTitle}>{instance.name || instance.id}</div>
          {sub ? <div className={cardStyles.cardSub}>{sub}</div> : null}
        </div>
        <StatusPill tone={instanceTone(key)}>{instanceLabel(key)}</StatusPill>
      </div>
      <div className={cardStyles.cardFoot}>
        <span className={cardStyles.cardSub}>
          {ip ? <code style={{ fontSize: 'var(--vn-font-size-sm)' }}>{ip}</code> : '—'}
        </span>
        <span className={cardStyles.openHint}>
          Open <ArrowRight size={14} />
        </span>
      </div>
    </div>
  );
}

function StatusCard({ label, value, tone }: { label: string; value: number; tone: 'success' | 'muted' | 'danger' | 'warning' }) {
  const color = tone === 'success' ? 'var(--vn-success-solid)' : tone === 'danger' ? 'var(--vn-danger-solid)' : tone === 'warning' ? 'var(--vn-warning-solid)' : 'var(--vn-fg-muted)';
  return (
    <Card flat>
      <CardBody>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} /> {label}
        </div>
        <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 700 }}>{value}</div>
      </CardBody>
    </Card>
  );
}
